import type {
  MachineRuleInput,
  PaymentInput,
  PaymentMachineInput,
  PaymentMethodValue,
  ReceivableStatusValue,
} from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const RECEIVABLES_ROUTES = {
  MACHINES: '/recebimentos/maquininhas',
  LIST: '/recebimentos',
} as const;

const BASE = '/api/v1/receivables';

export interface Acquirer {
  id: string;
  name: string;
  slug: string;
}

export interface MachineRule {
  method: PaymentMethodValue;
  installments: number;
  feePercent: number;
  daysToReceive: number;
}

export interface PaymentMachine {
  id: string;
  companyId: string;
  acquirerId: string;
  acquirerName: string;
  label: string;
  nickname: string | null;
  isActive: boolean;
  usageCount: number;
  rules: MachineRule[];
  createdAt: string;
  updatedAt: string;
}

export interface Receivable {
  id: string;
  companyId: string;
  unitId: string | null;
  unitName: string | null;
  appointmentId: string;
  customerId: string | null;
  customerName: string | null;
  customerImage: string | null;
  professionalId: string | null;
  professionalName: string | null;
  method: PaymentMethodValue;
  machineName: string | null;
  installment: number;
  installments: number;
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
  dueDate: string;
  status: ReceivableStatusValue;
  receivedAt: string | null;
  receivedAmount: number | null;
  difference: number;
  anticipatedAt: string | null;
  anticipationFee: number | null;
  note: string | null;
  isOverdue: boolean;
  createdAt: string;
}

export interface CashFlowSlice {
  label: string;
  amount: number;
}

export interface CashFlow {
  today: number;
  tomorrow: number;
  nextSevenDays: number;
  nextThirtyDays: number;
  receivedToday: number;
  receivedThisMonth: number;
  overdue: number;
  pendingTotal: number;
  byMethod: { method: PaymentMethodValue; amount: number }[];
  byMachine: CashFlowSlice[];
  byProfessional: CashFlowSlice[];
}

export interface ReceivablesQuery {
  page: number;
  perPage?: number;
  status?: 'all' | ReceivableStatusValue | 'overdue';
  method?: PaymentMethodValue;
  machineId?: string;
  professionalId?: string;
  unitId?: string;
  from?: string;
  to?: string;
}

export const receivablesApi = {
  listAcquirers: () => apiClient.get<{ data: Acquirer[] }>(`${BASE}/acquirers`),

  listMachines: (onlyActive = false) =>
    apiClient.get<{ data: PaymentMachine[] }>(
      `${BASE}/machines${onlyActive ? '?onlyActive=true' : ''}`,
    ),

  createMachine: (input: PaymentMachineInput) =>
    apiClient.post<PaymentMachine>(`${BASE}/machines`, input),
  updateMachine: (id: string, input: Partial<PaymentMachineInput>) =>
    apiClient.patch<PaymentMachine>(`${BASE}/machines/${id}`, input),
  removeMachine: (id: string) => apiClient.delete<{ message: string }>(`${BASE}/machines/${id}`),
  replaceRules: (id: string, rules: MachineRuleInput[]) =>
    apiClient.put<PaymentMachine>(`${BASE}/machines/${id}/rules`, { rules }),

  listReceivables(params: ReceivablesQuery) {
    const query = new URLSearchParams({ page: String(params.page) });
    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.method) query.set('method', params.method);
    if (params.machineId) query.set('machineId', params.machineId);
    if (params.professionalId) query.set('professionalId', params.professionalId);
    if (params.unitId) query.set('unitId', params.unitId);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);

    return apiClient.get<{ data: Receivable[]; meta: PaginationMeta }>(
      `${BASE}?${query.toString()}`,
    );
  },

  cashFlow: (unitId?: string) =>
    apiClient.get<CashFlow>(`${BASE}/cash-flow${unitId ? `?unitId=${unitId}` : ''}`),

  paymentsOf: (appointmentId: string) =>
    apiClient.get<{
      data: {
        id: string;
        method: PaymentMethodValue;
        machineId: string | null;
        machineName: string | null;
        installments: number;
        amount: number;
        createdAt: string;
      }[];
    }>(`${BASE}/appointments/${appointmentId}/payments`),
};

const KEYS = {
  all: ['receivables'] as const,
  acquirers: ['receivables', 'acquirers'] as const,
  machines: ['receivables', 'machines'] as const,
  list: ['receivables', 'list'] as const,
  cashFlow: ['receivables', 'cash-flow'] as const,
  payments: (appointmentId: string) => ['receivables', 'payments', appointmentId] as const,
};

export function useAcquirers() {
  return useQuery({
    queryKey: KEYS.acquirers,
    queryFn: () => receivablesApi.listAcquirers(),
    // Catalogo do sistema: muda de release em release, nao de minuto em minuto.
    staleTime: 60 * 60 * 1000,
  });
}

export function useMachines(onlyActive = false) {
  return useQuery({
    queryKey: [...KEYS.machines, onlyActive],
    queryFn: () => receivablesApi.listMachines(onlyActive),
    placeholderData: (previous) => previous,
  });
}

export function useReceivables(params: ReceivablesQuery) {
  return useQuery({
    queryKey: [...KEYS.list, params],
    queryFn: () => receivablesApi.listReceivables(params),
    placeholderData: (previous) => previous,
  });
}

export function useCashFlow(unitId?: string) {
  return useQuery({
    queryKey: [...KEYS.cashFlow, unitId ?? ''],
    queryFn: () => receivablesApi.cashFlow(unitId),
    placeholderData: (previous) => previous,
  });
}

export function useAppointmentPayments(appointmentId: string | undefined) {
  return useQuery({
    queryKey: KEYS.payments(appointmentId ?? ''),
    queryFn: () => receivablesApi.paymentsOf(appointmentId as string),
    enabled: Boolean(appointmentId),
  });
}

function useReceivablesMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateMachine() {
  return useReceivablesMutation((input: PaymentMachineInput) =>
    receivablesApi.createMachine(input),
  );
}

export function useUpdateMachine() {
  return useReceivablesMutation(
    ({ id, input }: { id: string; input: Partial<PaymentMachineInput> }) =>
      receivablesApi.updateMachine(id, input),
  );
}

export function useDeleteMachine() {
  return useReceivablesMutation((id: string) => receivablesApi.removeMachine(id));
}

export function useReplaceMachineRules() {
  return useReceivablesMutation(({ id, rules }: { id: string; rules: MachineRuleInput[] }) =>
    receivablesApi.replaceRules(id, rules),
  );
}

/** O que a tela de venda precisa saber para sugerir o pagamento. */
export type SalePayment = PaymentInput;
